import { useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { CoachVeeraSidebar } from "@/components/chat/CoachVeeraSidebar";
import { useAuth } from "@/hooks/useAuth";
import {
  COACH_VEERA_PANEL_ID,
  COACH_VEERA_PANEL_WIDTH_STYLE,
  useCoachVeeraPanel,
} from "@/hooks/useCoachVeeraPanel";
import { useTrialAccess } from "@/hooks/useTrialAccess";
import { useIsMobile } from "@/hooks/use-mobile";
import { claimCoachMobileWorkspaceHistory } from "@/lib/coachMobileWorkspaceHistory";
import { cn } from "@/lib/utils";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");
function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => !element.hasAttribute("inert") && element.getAttribute("aria-hidden") !== "true");
}

function focusIsInOwnedMenuPortal(panel: HTMLElement, active: Element | null) {
  if (!(active instanceof HTMLElement) || panel.contains(active)) return false;
  const menu = active.closest<HTMLElement>('[role="menu"][aria-labelledby]');
  const triggerId = menu?.getAttribute("aria-labelledby");
  const trigger = triggerId ? document.getElementById(triggerId) : null;
  return Boolean(trigger && panel.contains(trigger));
}

function focusConnectedCoachVeeraLauncher() {
  const launcher = Array.from(document.querySelectorAll<HTMLElement>(
    `[aria-controls="${COACH_VEERA_PANEL_ID}"]`,
  )).find((candidate) => (
    candidate.isConnected &&
    !candidate.closest("[inert]") &&
    candidate.getAttribute("aria-hidden") !== "true"
  ));
  launcher?.focus();
}

const ORDINARY_COACH_ROUTE_PATTERNS = [
  "/coach/overview", "/coach/record", "/coach/leaders", "/coach/leaders/:leaderId",
  "/coach/leaders/:leaderId/record", "/coach/leaders/:leaderId/synthesis/:behaviorId",
  "/coach/reviews", "/coach/hub", "/coach/hub/identity", "/coach/hub/philosophy",
  "/coach/hub/frameworks", "/coach/hub/guardrails", "/coach/hub/materials",
  "/coach/hub/lens", "/coach/hub/record", "/coach/hub/alignment", "/coach/hub/veera", "/coach/identity",
  "/coach/identity/guided-form", "/coach/messages", "/coach/apps", "/coach/product-feedback",
  "/coach/meetings", "/coach/meetings/:meetingId", "/coach/meetings/:meetingId/reflect",
  "/coach/calendar", "/coach/calls", "/coach/reflect/:meetingId", "/coach/philosophy",
  "/coach/my-voice", "/coach/settings", "/coach/help", "/coach/review/:type/:leaderId",
  "/coach/review/sequence/:leaderId",
  "/coach/post-meeting/:transcriptId", "/coach/dashboard", "/coach/transcripts",
  "/coach/insights", "/coach/notifications",
] as const;

const isEligibleCoachRoute = (pathname: string) => ORDINARY_COACH_ROUTE_PATTERNS.some(
  (pattern) => matchPath({ path: pattern, end: true }, pathname),
);

export function CoachVeeraPanelHost() {
  const location = useLocation();
  const { user, roles, loading: authLoading } = useAuth();
  const panel = useCoachVeeraPanel();
  const { hasOpened, resetBoundary } = panel;
  const authorizedUserIdRef = useRef<string | null>(null);
  const hasCoachRole = roles.includes("coach");
  const routeEligible = isEligibleCoachRoute(location.pathname);
  const identityEligible = Boolean(user) && hasCoachRole;
  const authorizedUserChanged = Boolean(
    authorizedUserIdRef.current && user?.id && authorizedUserIdRef.current !== user.id,
  );

  useEffect(() => {
    if (authorizedUserChanged) {
      authorizedUserIdRef.current = user?.id ?? null;
      if (hasOpened) resetBoundary();
      return;
    }
    if (identityEligible && user?.id) authorizedUserIdRef.current = user.id;
    if (!authLoading && !user) authorizedUserIdRef.current = null;
  }, [authLoading, authorizedUserChanged, hasOpened, identityEligible, resetBoundary, user]);

  useEffect(() => {
    if (hasOpened && (!routeEligible || (!authLoading && !identityEligible))) resetBoundary();
  }, [authLoading, hasOpened, identityEligible, resetBoundary, routeEligible]);

  if (authorizedUserChanged || !hasOpened || !routeEligible || (!authLoading && !identityEligible)) return null;

  return <CoachVeeraPanelContent />;
}

function CoachVeeraPanelContent() {
  const navigate = useNavigate();
  const access = useTrialAccess({ role: "coach" });
  const panel = useCoachVeeraPanel();
  const { closePanel, context, open, restoreFocus } = panel;
  const [hasDraft, setHasDraft] = useState(false);
  const wasOpenRef = useRef(open);
  const panelRef = useRef<HTMLElement | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      requestAnimationFrame(() => {
        restoreFocus();
        const active = document.activeElement;
        if (!active || active === document.body || panelRef.current?.contains(active)) {
          focusConnectedCoachVeeraLauncher();
        }
      });
    }
    wasOpenRef.current = open;
  }, [open, restoreFocus]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === "Escape" && !hasDraft) {
        event.preventDefault();
        closePanel();
        return;
      }
      if (!isMobile || event.key !== "Tab") return;
      const panelElement = panelRef.current;
      if (!panelElement) return;
      if (focusIsInOwnedMenuPortal(panelElement, document.activeElement)) return;
      const focusable = getFocusableElements(panelElement);
      if (focusable.length === 0) {
        event.preventDefault();
        panelElement.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panelElement.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panelElement.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closePanel, hasDraft, isMobile, open]);

  useEffect(() => {
    if (!isMobile || !open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => {
      const panelElement = panelRef.current;
      if (!panelElement) return;
      (getFocusableElements(panelElement)[0] ?? panelElement).focus();
    });
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobile, open]);

  useEffect(() => {
    if (!isMobile || !open) return;
    return claimCoachMobileWorkspaceHistory("coach-veera", closePanel);
  }, [closePanel, isMobile, open]);

  const state = access.presentation.state;
  const expired = state === "expired";
  const unknown = state === "unknown";
  const composerDisabled = access.isLoading || unknown || expired || !access.canCreatePremiumActivity;
  const lockedMessage = access.isLoading
    ? "Checking access…"
    : expired
      ? "Your trial has ended. Your previous conversations are still available."
      : unknown
        ? "We couldn't confirm access."
        : !access.canCreatePremiumActivity
          ? "New Coach Veera messages are not available with your current access."
          : undefined;

  const pageContext = context
    ? {
        pageName: context.pageName,
        ...(context.leaderId ? { leaderId: context.leaderId } : {}),
        ...(context.relationshipRevision
          ? { relationshipRevision: context.relationshipRevision }
          : {}),
        data: {
          source: context.source,
          ...context.data,
          ...(context.accessGeneration
            ? { accessGeneration: context.accessGeneration }
            : {}),
        },
      }
    : { pageName: "coach" };
  const selectedLeaderName = typeof context?.data?.selectedLeaderName === "string"
    ? context.data.selectedLeaderName.trim()
    : "";
  const leaderContextName = context?.presentation?.kind === "leader_context"
    ? context.presentation.leaderName.trim()
    : "";
  const conversationDescription = leaderContextName
    ? `Leader context available: ${leaderContextName}`
    : "Portfolio coaching conversation";
  const contextLabel = leaderContextName
    ? `Ask about ${leaderContextName}, or ask a portfolio question.`
    : selectedLeaderName
      ? `Opened from ${selectedLeaderName}`
      : undefined;
  const planParams = new URLSearchParams({ trial_plan: "intelligence_solo" });
  if (access.primaryLeaderId) planParams.set("trial_leader", access.primaryLeaderId);

  return (
    <aside
      ref={panelRef}
      id={COACH_VEERA_PANEL_ID}
      data-testid="coach-veera-panel"
      data-coach-workspace-rail="true"
      role={isMobile && open ? "dialog" : undefined}
      aria-label="Coach Veera"
      aria-modal={isMobile && open ? "true" : undefined}
      aria-hidden={!open}
      {...(!open ? { inert: "" } : {})}
      tabIndex={isMobile && open ? -1 : undefined}
      style={COACH_VEERA_PANEL_WIDTH_STYLE}
      className={cn(
        "fixed inset-y-0 right-0 z-50 w-full bg-violet-50 transition-transform [transition-duration:250ms] ease-out motion-reduce:transition-none md:w-[var(--coach-veera-panel-width)] md:border-l md:border-violet-200 md:shadow-xl",
        open ? "translate-x-0" : "pointer-events-none translate-x-full",
      )}
    >
      <CoachVeeraSidebar
        className="h-[100dvh] bg-violet-50"
        fullWidth
        surface="panel"
        pageContext={pageContext}
        conversationDescription={conversationDescription}
        contextLabel={contextLabel}
        onClose={closePanel}
        isActive={open}
        composerDisabled={composerDisabled}
        lockedMessage={lockedMessage}
        onViewPlans={expired || (!access.isLoading && !unknown && !access.canCreatePremiumActivity)
          ? () => {
              closePanel();
              navigate(`/coach/settings?${planParams.toString()}#leaders`, { replace: isMobile });
            }
          : undefined}
        onDraftStateChange={setHasDraft}
      />
    </aside>
  );
}
